<template>
  <section ref="screenRoot" class="page admin-screen" data-test="admin-onboard">
    <div class="anim" data-delay="80">
      <Eyebrow>Admin · onboarding</Eyebrow>
      <DisplayHeading lead="Onboard a Jira" accent="board" />
      <p class="display-sub">
        Paste the URL of the Jira board you want on the dashboard. We'll
        backfill every closed sprint, drop a row in DynamoDB for each, and
        set up the daily cron schedule that keeps the active sprint fresh.
      </p>
      <AdminTabs />
    </div>

    <div class="anim onboard-form" data-delay="160">
      <label class="field">
        <span class="lbl-mono">Board / project</span>
        <input
          v-model="urlInput"
          type="text"
          class="url-input"
          placeholder="https://foodics-products.atlassian.net/jira/software/c/projects/DA/boards/1123 — or a project key like DA"
          :disabled="isBusy"
          autocomplete="off"
          spellcheck="false"
          data-test="onboard-url-input"
          @keyup.enter="onSubmit"
        />
      </label>

      <div v-if="parsed && parsed.ok && parsed.input.kind === 'board'" class="parsed-preview" data-test="onboard-parsed">
        <div class="parsed-row"><span class="lbl-mono">Tenant</span><code>{{ parsed.input.value.jira_base_url }}</code></div>
        <div class="parsed-row"><span class="lbl-mono">Board id</span><code>{{ parsed.input.value.board_id }}</code></div>
        <div class="parsed-row">
          <span class="lbl-mono">Owning project</span>
          <code>{{ parsed.input.value.owning_project_per_jira }}</code>
          <span class="parsed-hint">per Jira — rows are created under whichever projects' tickets actually appear in each sprint.</span>
        </div>
      </div>

      <div v-else-if="parsed && parsed.ok && parsed.input.kind === 'project'" class="parsed-preview" data-test="onboard-parsed">
        <div class="parsed-row">
          <span class="lbl-mono">Project</span>
          <code>{{ parsed.input.project_key }}</code>
          <span class="parsed-hint">we'll list this project's boards next.</span>
        </div>
      </div>

      <div v-else-if="parsed && !parsed.ok && urlInput.trim()" class="parse-error" data-test="onboard-parse-error">
        {{ parsed.error }}
      </div>

      <button
        type="button"
        class="onboard-btn"
        :disabled="!canSubmit"
        data-test="onboard-submit"
        @click="onSubmit"
      >
        <span v-if="phase === 'starting'">Submitting…</span>
        <span v-else-if="phase === 'polling'">Onboarding…</span>
        <span v-else>Onboard</span>
      </button>
    </div>

    <!-- Project-level discovery → board picker -->
    <div v-if="discovering" class="anim picker-card" data-delay="160" data-test="onboard-discovering">
      Looking up boards…
    </div>
    <div v-else-if="discoverError" class="error-state anim" data-delay="160" data-test="onboard-discover-error">
      <p class="error-title">Discovery failed</p>
      <p class="error-msg">{{ discoverError }}</p>
    </div>
    <div v-else-if="discovery" class="anim picker-card" data-delay="160">
      <BoardPicker :response="discovery" :busy="isBusy" @confirm="onPickerConfirm" />
    </div>

    <!-- Multi-board queue overview -->
    <ul v-if="queueItems.length > 1" class="queue-list anim" data-delay="120" data-test="onboard-queue">
      <li v-for="(q, i) in queueItems" :key="q.board_id" :class="['queue-row', `q-${q.state}`]">
        <span class="queue-name">{{ q.name }}</span>
        <code class="queue-id">#{{ q.board_id }}</code>
        <span class="queue-state" :data-test="`queue-state-${i}`">
          {{ q.state }}<template v-if="q.error"> — {{ q.error }}</template>
        </span>
      </li>
    </ul>

    <!-- Progress / result card -->
    <div v-if="phase === 'error'" class="error-state anim" data-delay="160">
      <p class="error-title">Onboarding failed</p>
      <p class="error-msg">{{ errorMessage || "Something went wrong — check Windmill." }}</p>
    </div>

    <div v-else-if="kickoff" class="progress-card anim" data-delay="160" data-test="onboard-progress">
      <header class="progress-head">
        <div>
          <span class="lbl-mono">Board id</span>
          <code class="proj-key">{{ kickoff.board_id }}</code>
          <span class="muted">·</span>
          <span class="lbl-mono">Tenant</span>
          <code>{{ kickoff.jira_base_url }}</code>
        </div>
        <span :class="['status-pill', statusClass]" data-test="onboard-status">{{ statusLabel }}</span>
      </header>

      <div v-if="job?.result?.status === 'already_onboarded'" class="already-onboarded">
        Board <code>{{ kickoff.board_id }}</code> is already onboarded — no backfill needed.
        Daily schedule: <code>{{ job.result.schedule_path }}</code>.
      </div>

      <template v-else>
        <div class="progress-stats">
          <div class="stat">
            <span class="stat-num" data-test="onboard-completed-count">{{ completedCount }}</span>
            <span class="stat-lbl">scored</span>
          </div>
          <div class="stat">
            <span class="stat-num">{{ runningCount }}</span>
            <span class="stat-lbl">running</span>
          </div>
          <div class="stat">
            <span class="stat-num">{{ sprintChildren.length }}</span>
            <span class="stat-lbl">discovered</span>
          </div>
        </div>

        <div class="progress-bar" :aria-valuenow="completedCount" :aria-valuemax="children.length || 1">
          <div class="progress-fill" :style="{ width: progressPercent + '%' }" />
        </div>

        <ul v-if="sprintChildren.length > 0" class="sprint-list" data-test="onboard-sprint-list">
          <li
            v-for="c in childrenSorted"
            :key="c.job_id"
            :class="['sprint-row', `status-${c.status}`, c.success === false ? 'failed' : '']"
          >
            <span class="sprint-id">#{{ c.sprint_id ?? "—" }}</span>
            <span class="sprint-status">
              <span :class="['dot', `dot-${c.status}`]" />
              {{ rowStatusLabel(c) }}
            </span>
          </li>
        </ul>
        <p v-else-if="phase === 'polling'" class="no-children-yet">
          Discovering sprints…
        </p>
      </template>

      <div v-if="phase === 'done' && job?.success" class="done-line">
        Done. Sprint rows are now in the dashboard under the projects whose
        tickets appeared in this board's sprints.
        <RouterLink :to="{ name: 'home' }" class="done-link">
          Open dashboard →
        </RouterLink>
      </div>
      <div v-else-if="phase === 'done' && job && job.success === false" class="done-line failed">
        The onboarding flow returned a failure. {{ job.error_brief || "" }}
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { RouterLink } from "vue-router";

import DisplayHeading from "@/components/scorecard/DisplayHeading.vue";
import Eyebrow from "@/components/scorecard/Eyebrow.vue";
import AdminTabs from "@/features/admin/AdminTabs.vue";
import BoardPicker from "@/features/admin/BoardPicker.vue";
import { useReveal } from "@/composables/useReveal";
import { useOnboardQueue } from "@/composables/useOnboardQueue";
import { useApi } from "@/composables/useApi";
import { parseOnboardInput, type OnboardParseResult } from "@/lib/jiraBoardUrl";
import {
  discoverBoards,
  type DiscoveredBoard,
  type DiscoverBoardsResponse,
  type OnboardChildJob,
  type OnboardJobStatus,
} from "@/api/admin";

const screenRoot = ref<HTMLElement | null>(null);
useReveal(screenRoot);

const { client } = useApi();
const urlInput = ref("");
const parsed = computed<OnboardParseResult | null>(() =>
  urlInput.value.trim() ? parseOnboardInput(urlInput.value) : null,
);

const queue = useOnboardQueue();
const { items: queueItems, queueRunning, startQueue } = queue;
// Existing template bindings (phase/errorMessage/kickoff/job/children) now
// come from the queue's underlying per-board lifecycle.
const { phase, errorMessage, kickoff, job, children } = queue.job;

const discovery = ref<DiscoverBoardsResponse | null>(null);
const discovering = ref(false);
const discoverError = ref<string | null>(null);

const isBusy = computed(
  () => discovering.value || queueRunning.value
    || phase.value === "starting" || phase.value === "polling",
);
const canSubmit = computed(() => !!parsed.value && parsed.value.ok && !isBusy.value);

async function onSubmit(): Promise<void> {
  if (!canSubmit.value || !parsed.value || !parsed.value.ok) return;
  const input = parsed.value.input;
  discovery.value = null;
  discoverError.value = null;

  if (input.kind === "board") {
    // Single board — a queue of one; same progress UI as before.
    await startQueue(input.value.jira_base_url, [
      { board_id: input.value.board_id, name: `Board ${input.value.board_id}` },
    ]);
    return;
  }

  discovering.value = true;
  try {
    discovery.value = await discoverBoards(client, input.project_key);
  } catch (err) {
    discoverError.value =
      (err as { message?: string }).message ?? "Board discovery failed.";
  } finally {
    discovering.value = false;
  }
}

async function onPickerConfirm(boards: DiscoveredBoard[]): Promise<void> {
  const resp = discovery.value;
  if (!resp || boards.length === 0) return;
  discovery.value = null; // collapse the picker; progress UI takes over
  await startQueue(
    resp.jira_base_url,
    boards.map((b) => ({ board_id: b.board_id, name: b.name })),
  );
}

const statusLabel = computed(() => {
  switch (phase.value) {
    case "starting": return "Submitting…";
    case "polling":  return job.value?.status === "running" ? "Running" : "Queued";
    case "done":     return job.value?.success ? "Done" : "Failed";
    case "error":    return "Error";
    default:         return "Idle";
  }
});

const statusClass = computed(() => {
  switch (phase.value) {
    case "done":  return job.value?.success ? "ok" : "err";
    case "error": return "err";
    case "polling":
    case "starting": return "running";
    default: return "idle";
  }
});

// Windmill's `/jobs/list?parent_job=<id>` returns the parent itself plus any
// helper sub-calls (e.g. the schedule-existence check) — none of those carry
// args.sprint_id. We only want the per-sprint scoring children in the table.
const sprintChildren = computed(
  () => children.value.filter((c) => c.sprint_id != null),
);

const completedCount = computed(
  () => sprintChildren.value.filter((c) => c.status === "completed" && c.success).length,
);
const runningCount = computed(
  () => sprintChildren.value.filter((c) => c.status === "running").length,
);
const progressPercent = computed(() => {
  const total = sprintChildren.value.length;
  if (total === 0) return phase.value === "done" ? 100 : 0;
  return Math.min(100, Math.round((completedCount.value / total) * 100));
});

const childrenSorted = computed(() => {
  // Order: running first, then queued, then completed (so the eye is drawn
  // to active work). Ties broken by sprint_id ascending for stability.
  const order: Record<OnboardJobStatus, number> = {
    running: 0, queued: 1, unknown: 2, completed: 3,
  };
  return [...sprintChildren.value].sort((a, b) => {
    const pa = order[a.status] ?? 9;
    const pb = order[b.status] ?? 9;
    if (pa !== pb) return pa - pb;
    return (a.sprint_id ?? 0) - (b.sprint_id ?? 0);
  });
});

function rowStatusLabel(c: OnboardChildJob): string {
  if (c.status === "completed") return c.success ? "scored" : "failed";
  if (c.status === "running") return "scoring…";
  if (c.status === "queued") return "queued";
  return c.status;
}

// Reset the input when the queue finishes so the user can onboard the next
// board without leaving the page. Keeps the result card visible until the
// URL changes again.
watch(queueRunning, (running, was) => {
  if (running) {
    // A run takes over the screen — drop any leftover discovery UI.
    discovery.value = null;
    discoverError.value = null;
  }
  if (was && !running) urlInput.value = "";
});
</script>

<style scoped>
.admin-screen { position: relative; }

.onboard-form {
  margin-top: 24px;
  display: grid;
  gap: 16px;
  background: var(--bg-elev);
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 24px;
}
.field { display: grid; gap: 8px; }
.field .lbl-mono {
  font-family: "JetBrains Mono", monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted-2);
}
.url-input {
  width: 100%;
  padding: 12px 14px;
  border: 1px solid var(--line);
  background: var(--bg);
  border-radius: 10px;
  font-family: "JetBrains Mono", monospace;
  font-size: 13px;
  color: var(--ink-1);
  transition: border-color var(--speed-quick) var(--ease);
}
.url-input:focus {
  outline: none;
  border-color: var(--brand);
}
.url-input:disabled { opacity: 0.6; cursor: not-allowed; }

.parsed-preview {
  display: grid;
  gap: 6px;
  padding: 12px 14px;
  background: var(--brand-tint);
  border: 1px solid color-mix(in srgb, var(--brand) 25%, transparent);
  border-radius: 10px;
}
.parsed-row {
  display: flex;
  align-items: baseline;
  gap: 12px;
  font-size: 13px;
}
.parsed-row .lbl-mono {
  flex: 0 0 80px;
  font-family: "JetBrains Mono", monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted-2);
}
.parsed-row code {
  font-family: "JetBrains Mono", monospace;
  font-size: 13px;
  color: var(--ink-1);
}
.parsed-row .parsed-hint {
  font-size: 11px;
  color: var(--muted-2);
  font-style: italic;
}

.parse-error {
  padding: 10px 14px;
  background: var(--red-soft);
  color: var(--red-ink);
  border-radius: 10px;
  font-size: 13px;
}

.onboard-btn {
  justify-self: start;
  background: var(--brand);
  color: white;
  border: 0;
  padding: 10px 20px;
  border-radius: 10px;
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: opacity var(--speed-quick) var(--ease);
}
.onboard-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.error-state {
  margin-top: 24px;
  padding: 16px;
  background: var(--red-soft);
  border: 1px solid color-mix(in srgb, var(--red) 30%, transparent);
  border-radius: 12px;
  color: var(--red-ink);
  display: grid; gap: 8px;
}
.error-title { font-weight: 500; margin: 0; }
.error-msg { font-size: 13px; margin: 0; color: var(--ink-2); }

.progress-card {
  margin-top: 24px;
  padding: 24px;
  background: var(--bg-elev);
  border: 1px solid var(--line);
  border-radius: 16px;
  display: grid;
  gap: 16px;
}
.progress-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}
.progress-head .lbl-mono {
  font-family: "JetBrains Mono", monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted-2);
  margin-right: 6px;
}
.progress-head code {
  font-family: "JetBrains Mono", monospace;
  font-size: 13px;
  color: var(--ink-1);
  margin-right: 12px;
}
.progress-head .proj-key { color: var(--brand); }
.progress-head .muted { color: var(--muted-2); margin-right: 12px; }

.status-pill {
  font-family: "JetBrains Mono", monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 999px;
}
.status-pill.idle    { background: var(--bg-tint); color: var(--ink-2); }
.status-pill.running { background: var(--brand-soft); color: var(--brand); }
.status-pill.ok      { background: var(--green-soft); color: var(--green-ink); }
.status-pill.err     { background: var(--red-soft); color: var(--red-ink); }

.already-onboarded {
  padding: 12px 14px;
  background: var(--bg-tint);
  border-radius: 10px;
  font-size: 13px;
  color: var(--ink-2);
}
.already-onboarded code {
  font-family: "JetBrains Mono", monospace;
  font-size: 12px;
  color: var(--ink-1);
}

.progress-stats {
  display: flex;
  gap: 32px;
}
.stat {
  display: grid;
  gap: 2px;
}
.stat-num {
  font-size: 28px;
  font-weight: 500;
  color: var(--ink-1);
  font-feature-settings: "tnum";
}
.stat-lbl {
  font-family: "JetBrains Mono", monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted-2);
}

.progress-bar {
  height: 6px;
  background: var(--bg-tint);
  border-radius: 999px;
  overflow: hidden;
}
.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--brand), color-mix(in srgb, var(--brand) 60%, transparent));
  transition: width 600ms ease-out;
}

.sprint-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 4px;
  max-height: 360px;
  overflow-y: auto;
}
.sprint-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: var(--bg);
  border: 1px solid var(--line-2);
  border-radius: 8px;
  font-size: 13px;
  transition: background var(--speed-quick) var(--ease);
}
.sprint-row .sprint-id {
  font-family: "JetBrains Mono", monospace;
  font-size: 12px;
  color: var(--muted-2);
}
.sprint-row .sprint-status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: "JetBrains Mono", monospace;
  font-size: 11px;
  letter-spacing: 0.04em;
  color: var(--ink-2);
}
.dot {
  width: 8px; height: 8px;
  border-radius: 50%;
  display: inline-block;
}
.dot-queued    { background: var(--muted-3, #c0c0c0); }
.dot-running   { background: var(--brand); animation: pulse 1.4s ease-in-out infinite; }
.dot-completed { background: var(--green); }
.dot-unknown   { background: var(--muted-3, #c0c0c0); }
.sprint-row.failed .dot-completed { background: var(--red); }
.sprint-row.failed .sprint-status { color: var(--red-ink); }

@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50%      { opacity: 0.5; transform: scale(0.8); }
}

.no-children-yet {
  font-family: "JetBrains Mono", monospace;
  font-size: 12px;
  color: var(--muted-2);
  margin: 0;
}

.done-line {
  padding: 12px 14px;
  background: var(--green-soft);
  color: var(--green-ink);
  border-radius: 10px;
  font-size: 13px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.done-line.failed {
  background: var(--red-soft);
  color: var(--red-ink);
}
.done-link {
  color: inherit;
  text-decoration: none;
  font-weight: 500;
}
.done-link:hover { text-decoration: underline; }

.picker-card {
  margin-top: 24px;
  padding: 24px;
  background: var(--bg-elev);
  border: 1px solid var(--line);
  border-radius: 16px;
  font-size: 13px;
  color: var(--ink-2);
}

.queue-list {
  margin-top: 24px;
  list-style: none;
  padding: 0;
  display: grid;
  gap: 6px;
}
.queue-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  background: var(--bg-elev);
  border: 1px solid var(--line);
  border-radius: 10px;
  font-size: 13px;
}
.queue-row .queue-name { color: var(--ink-1); flex: 1 1 auto; }
.queue-row .queue-id {
  font-family: "JetBrains Mono", monospace;
  font-size: 12px;
  color: var(--muted-2);
}
.queue-row .queue-state {
  font-family: "JetBrains Mono", monospace;
  font-size: 11px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--muted-2);
}
.queue-row.q-running .queue-state { color: var(--brand); }
.queue-row.q-done .queue-state    { color: var(--green-ink); }
.queue-row.q-failed .queue-state  { color: var(--red-ink); }
</style>
